import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { ButtonModule } from 'primeng/button';
import { CurrencyPipe, DatePipe, DecimalPipe, NgClass } from '@angular/common';
import { MenuItem, SharedModule } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { Menu, MenuModule } from 'primeng/menu';
import { CustomAction, CustomTableHeader } from '../../../core/models/custom-table.model';
import { StorageService } from '../../../core/services/storage.service';
import { PageParams } from '../../../core/models/page.model';
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from '../../../core/utils/utils';
import { countries } from '../../../../assets/countries';

/** Tri demandé par l'utilisateur : 1 = croissant, -1 = décroissant. */
export interface TableSort {
  field: string;
  order: number;
}

const NUMERIC_TYPES: CustomTableHeader['type'][] = ['currency', 'number', 'percent'];

@Component({
    selector: 'dynamic-table',
    imports: [
        ButtonModule, PaginatorModule, SharedModule, TableModule, TagModule, MenuModule,
        CurrencyPipe, DatePipe, DecimalPipe, NgClass
    ],
    templateUrl: './dynamic-table.component.html',
    styleUrl: './dynamic-table.component.scss'
})
export class DynamicTableComponent implements OnInit {

  @Input({ required: true }) headers: CustomTableHeader[] = [];
  @Input({ required: true }) data: any[] = [];
  @Input() pageParams: PageParams = { page: 0, first: 0, rows: 0, totalRecords: 0, rowPerPageOptions: DEFAULT_ROWS_PER_PAGE_OPTIONS };
  @Input() actions: CustomAction[] = [];
  @Input() scrollable = true;
  @Input() defaultSortField?: string;
  @Input() defaultSortOrder: number = 1;
  /** Affiche le voile de chargement de p-table. */
  @Input() loading = false;
  @Output() paginate = new EventEmitter<PaginatorState>();
  @Output() rowClick = new EventEmitter<any>();
  /** Tri côté serveur : les en-têtes `sortable` ne sont cliquables que si cette sortie est écoutée. */
  @Output() sortChange = new EventEmitter<TableSort>();

  items: MenuItem[] = [];
  selectedData: any;
  sortField?: string;
  sortOrder = 1;

  constructor(private readonly tokenManager: StorageService) {}

  getNestedValue(obj: any, path: string | undefined): any {
    if (!path) return undefined;
    return path.split('.').reduce((acc, part) => acc?.[part], obj);
  }

  /** Alignement de la colonne : les montants et nombres sont à droite par défaut. */
  alignOf(header: CustomTableHeader): 'left' | 'center' | 'right' {
    if (header.align) return header.align;
    return NUMERIC_TYPES.includes(header.type) ? 'right' : 'center';
  }

  isSortable(header: CustomTableHeader): boolean {
    return !!header.sortable && this.sortChange.observed;
  }

  onSort(header: CustomTableHeader) {
    if (!this.isSortable(header)) return;
    this.sortOrder = this.sortField === header.key ? -this.sortOrder : 1;
    this.sortField = header.key;
    this.sortChange.emit({field: header.key, order: this.sortOrder});
  }

  /** Montant signé : « − » si le champ `signKey` vaut DEBIT, « + » sinon. */
  isDebit(item: any, header: CustomTableHeader): boolean {
    return `${this.getNestedValue(item, header.signKey) ?? ''}`.toUpperCase() === 'DEBIT';
  }

  ngOnInit() {
    this.sortField = this.defaultSortField;
    this.sortOrder = this.defaultSortOrder;
    if (this.actions.length > 0) {
      const jwt = this.tokenManager.getToken();
      this.actions = this.actions.filter(action => {
        const hasRole = !action.role || (jwt && jwt.authorities && jwt.authorities.some(role => action.role === role));
        const isVisible = typeof action.visible === 'boolean' ? action.visible : true;
        return hasRole && isVisible;
      });
    }
  }

  getActionsForRow(rowData: any): MenuItem[] {
    return this.actions
      .filter(action => this.isActionVisible(action, rowData))
      .map(action => ({
        label: action.label,
        icon: action.icon,
        command: () => action.emit(rowData)
      }));
  }

  isActionVisible(action: CustomAction, item: any): boolean {
    if (!action.visible) return true;
    if (typeof action.visible === 'boolean') return action.visible;
    if (typeof action.visible === 'function') return action.visible(item);
    return true;
  }

  getStatusIcon(status: string): string {
    if (!status) return 'pi pi-info-circle';
    const s = status.toUpperCase();
    if (s.includes('VALIDE') || s.includes('ACTIF') || s === 'SUCCESS' || s.includes('ACTIVE')) return 'pi pi-check-circle';
    if (s.includes('ATTENTE') || s.includes('PENDING')) return 'pi pi-clock';
    if (s.includes('ANNULE') || s.includes('INACTIVE') || s.includes('REJETE')) return 'pi pi-times-circle';
    return 'pi pi-info-circle';
  }

  getTagSeverity(status: string | undefined): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
    if (!status) return 'secondary';
    const s = status.toUpperCase();
    if (s.includes('VALIDE') || s.includes('ACTIF') || s === 'SUCCESS' || s.includes('ACTIVE')) return 'success';
    if (s.includes('ATTENTE') || s.includes('PENDING')) return 'warn';
    if (s.includes('ANNULE') || s.includes('INACTIVE') || s.includes('REJETE')) return 'danger';
    return 'info';
  }

  emit(data: any, $event: MouseEvent, menu: Menu) {
    this.selectedData = data;
    menu.model = this.getActionsForRow(data);
    menu.toggle($event);
  }

  onRowClick(rowData: any) {
    this.rowClick.emit(rowData);
  }

  getCountryCode(countryName: string): string {
    if (!countryName) return '';
    const country = countries.find(c => c.country.toLowerCase() === countryName.toLowerCase());
    return country?.code?.toUpperCase() || '';
  }

  getCountryName(countryCode: string): string {
    if (!countryCode) return '';

    // Si c'est un code pays (2 caractères), chercher le nom
    if (countryCode.length === 2) {
      const country = countries.find(c => c.code?.toUpperCase() === countryCode.toUpperCase());
      return country?.country || countryCode;
    }

    // Sinon, c'est déjà un nom
    return countryCode;
  }
}
